/* eslint-disable react-refresh/only-export-components --
   AuthProvider and the useAuth hook are intentionally co-located; this is the
   idiomatic React context pattern and the fast-refresh caveat is acceptable. */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import { api, getToken, setToken, clearToken, onSessionChange } from './api.js'
import {createAuthSessionReconciler} from './authSessionReconciler.js'
import {getSessionGeneration, sessionChangedError} from './sessionGeneration.js'

/**
 * Auth context. Holds the current user + token status and exposes
 * login/logout. On mount it validates any stored token via POST
 * /api/auth/me so a page refresh keeps the session without re-login.
 */
const AuthContext = createContext(null)

// Login responses across environments have varied; accept the common shapes.
function extractToken(res) {
  return (
    res?.token ||
    res?.jwt ||
    res?.access_token ||
    res?.accessToken ||
    res?.data?.token ||
    null
  )
}

function extractUser(res) {
  return res?.user || res?.data?.user || res?.account || res?.me || null
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [status, setStatus] = useState('loading') // loading | authed | anon

  // Reconcile the shared session on first load and actual cross-tab changes.
  // Credentials stay inside the reconciler closure, never in React keys/state.
  useEffect(() => {
    const session = createAuthSessionReconciler({
      readToken: getToken,
      subscribe: onSessionChange,
      readMe: () => api.me(),
      clearToken,
      extractUser,
      onState: next => { setUser(next.user); setStatus(next.status) },
    })
    session.start()
    return () => session.dispose()
  }, [])

  const login = useCallback(async (username, password) => {
    const startedGeneration = getSessionGeneration()
    const res = await api.login(username, password)
    if (startedGeneration !== getSessionGeneration()) throw sessionChangedError()
    const token = extractToken(res)
    if (!token) {
      throw new Error(
        res?.error || 'Login succeeded but no session token was returned.'
      )
    }
    setToken(token)
    const loginGeneration = getSessionGeneration()
    // Prefer the user from the login payload; otherwise confirm via /me.
    let u = extractUser(res)
    if (!u) {
      try {
        u = extractUser(await api.me())
      } catch {
        /* token is set; /me may be flaky — proceed with a minimal user */
      }
    }
    if (loginGeneration !== getSessionGeneration() || getToken() !== token) throw sessionChangedError()
    setUser(u || { username })
    setStatus('authed')
    return u
  }, [])

  const logout = useCallback(() => {
    clearToken()
    setUser(null)
    setStatus('anon')
  }, [])

  const value = useMemo(
    () => ({
      user,
      status,
      isAuthed: status === 'authed',
      isLoading: status === 'loading',
      login,
      logout,
    }),
    [user, status, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
